// Minimal audio player. Replaces the native controls of <audio controls> in articles.
// Without JS the native player stays. Seek and volume are native range inputs, so keyboard
// and screen readers work without extra code.
(() => {
  const init = () => {
    const audios = document.querySelectorAll('.p-prose audio[controls]');
    if (!audios.length) return;

    const fmt = (t) => (Number.isFinite(t)
      ? `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(Math.floor(t % 60)).padStart(2, '0')}`
      : '--:--');
    // iOS Safari ignores audio.volume (hardware buttons only): show the slider only where it works
    const canSetVolume = (() => { const t = new Audio(); t.volume = 0.5; return t.volume === 0.5; })();
    const readVolume = () => {
      try { const v = parseFloat(localStorage.getItem('d-volume')); return v >= 0 && v <= 1 ? v : 1; } catch (e) { return 1; }
    };
    const fillRange = (input, ratio) => input.style.setProperty('--v', `${(ratio || 0) * 100}%`);
    const volumes = [];

    audios.forEach((audio) => {
      audio.controls = false;
      audio.preload = 'metadata';
      const ui = document.createElement('div');
      ui.className = 'p-audio';
      ui.setAttribute('role', 'group');
      ui.setAttribute('aria-label', 'Audio player');
      ui.innerHTML = '<button class="p-audio__toggle" type="button" aria-label="Play"><span class="p-audio__icon"></span></button>'
        + '<input class="p-audio__range p-audio__seek" type="range" min="0" max="0" step="1" value="0" aria-label="Seek" disabled>'
        + '<span class="p-audio__time" aria-hidden="true">--:-- / --:--</span>'
        + (canSetVolume ? '<input class="p-audio__range p-audio__vol" type="range" min="0" max="1" step="0.05" aria-label="Volume">' : '');
      audio.after(ui);
      const [toggle, seek, time, vol] = ui.children;

      let dragging = false;
      const render = () => {
        const d = audio.duration;
        if (Number.isFinite(d) && seek.max !== String(Math.floor(d))) { seek.max = Math.floor(d); seek.disabled = false; }
        if (!dragging) seek.value = audio.currentTime;
        fillRange(seek, d ? audio.currentTime / d : 0);
        time.textContent = `${fmt(audio.currentTime)} / ${fmt(d)}`;
        seek.setAttribute('aria-valuetext', `${fmt(audio.currentTime)} of ${fmt(d)}`);
      };

      // Media failure: drop the custom UI and bring the native player back
      let failed = false;
      const fallback = () => {
        if (failed) return;
        failed = true;
        ui.remove();
        audio.controls = true;
        const i = volumes.findIndex((p) => p.audio === audio);
        if (i >= 0) volumes.splice(i, 1);
      };
      audio.addEventListener('error', fallback);
      const sources = audio.querySelectorAll('source'); // <source> children report load failures themselves
      if (sources.length) sources[sources.length - 1].addEventListener('error', fallback);
      toggle.addEventListener('click', () => {
        if (!audio.paused) { audio.pause(); return; }
        const p = audio.play();
        if (p && p.catch) {
          p.catch((err) => {
            // AbortError (interrupted by pause / new load) and NotAllowedError (autoplay policy) keep the UI
            if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
            fallback();
          });
        }
      });
      audio.addEventListener('play', () => { toggle.setAttribute('aria-label', 'Pause'); ui.classList.add('is-playing'); });
      audio.addEventListener('pause', () => { toggle.setAttribute('aria-label', 'Play'); ui.classList.remove('is-playing'); });
      ['loadedmetadata', 'durationchange', 'timeupdate', 'ended'].forEach((ev) => audio.addEventListener(ev, render));

      // seeking: mouse drag / click keep 1s precision
      seek.addEventListener('input', () => {
        dragging = true;
        audio.currentTime = parseFloat(seek.value);
        render();
      });
      seek.addEventListener('change', () => { dragging = false; });
      // arrow keys move 5s instead of the native 1 step
      const KEY_STEP = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 };
      seek.addEventListener('keydown', (e) => {
        if (!(e.key in KEY_STEP) || !audio.duration) return;
        e.preventDefault();
        audio.currentTime = Math.min(audio.duration, Math.max(0, audio.currentTime + KEY_STEP[e.key]));
        render();
      });

      if (vol) {
        volumes.push({ audio, vol });
        audio.volume = readVolume();
        vol.value = audio.volume;
        fillRange(vol, audio.volume);
        vol.setAttribute('aria-valuetext', `${Math.round(audio.volume * 100)}%`);
        vol.addEventListener('input', () => {
          const v = parseFloat(vol.value);
          volumes.forEach((p) => {
            p.audio.volume = v;
            p.vol.value = v;
            fillRange(p.vol, v);
            p.vol.setAttribute('aria-valuetext', `${Math.round(v * 100)}%`);
          });
          try { localStorage.setItem('d-volume', String(v)); } catch (e) {}
        });
      }
      render();
      // failed before this script ran: a media error, or every <source> candidate failed
      // (that path ends in NETWORK_NO_SOURCE without setting audio.error)
      if (audio.error || audio.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) fallback();
    });
  };
  // Only one article <audio> plays at a time: starting one pauses the others.
  // Covers native players too (e.g. after a fallback). Embeds in iframes cannot be controlled.
  document.addEventListener('play', (e) => {
    if (!(e.target instanceof HTMLAudioElement) || !e.target.closest('.p-prose')) return;
    document.querySelectorAll('.p-prose audio').forEach((other) => {
      if (other !== e.target && !other.paused) other.pause();
    });
  }, true); // media events don't bubble, so listen in the capture phase

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
